import React, { useCallback, useRef } from 'react'

import { TextInput } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, func, string,
} from 'prop-types'

import { CODE_LENGTH } from 'Pods/LazyLoaded/AiSignInModal/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

function Inputs({ digits, hasError, onDigitsChange }) {
  const t = useTranslation()
  const inputsRef = useRef([])

  const focusInput = useCallback((index) => {
    const target = inputsRef.current[index]
    if (target) {
      target.focus()
      target.select()
    }
  }, [])

  const setDigit = useCallback((index, value) => {
    const next = [...digits]
    next[index] = value
    onDigitsChange(next)
  }, [digits, onDigitsChange])

  const handleChange = useCallback((index, rawValue) => {
    const value = rawValue.replace(/\D/g, '')
    if (!value) {
      setDigit(index, '')
      return
    }
    // Typing into a box overwrites it and moves on; a multi-char value (autofill)
    // spreads across the remaining boxes.
    const chars = value.split('')
    const next = [...digits]
    let cursor = index
    chars.forEach((char) => {
      if (cursor < CODE_LENGTH) {
        next[cursor] = char
        cursor += 1
      }
    })
    onDigitsChange(next)
    focusInput(Math.min(cursor, CODE_LENGTH - 1))
  }, [digits, onDigitsChange, focusInput, setDigit])

  const handleKeyDown = useCallback((index, event) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (digits[index]) {
        setDigit(index, '')
      } else if (index > 0) {
        setDigit(index - 1, '')
        focusInput(index - 1)
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      focusInput(index - 1)
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      focusInput(index + 1)
    }
  }, [digits, setDigit, focusInput])

  const handlePaste = useCallback((event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH)
    if (!pasted) return
    event.preventDefault()
    const next = Array.from({ length: CODE_LENGTH }, (_, i) => pasted[i] || '')
    onDigitsChange(next)
    focusInput(Math.min(pasted.length, CODE_LENGTH - 1))
  }, [onDigitsChange, focusInput])

  return (
    <div className="ai-sign-in-modal__code-inputs">
      {digits.map((digit, index) => (
        <TextInput
          isPilot
          /* eslint-disable-next-line react/no-array-index-key */
          key={index}
          ref={(el) => { inputsRef.current[index] = el }}
          id={`ai-sign-in-code-${index}`}
          type="primary"
          inputType="text"
          size="medium"
          maxLength={1}
          value={digit}
          isWarning={hasError}
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label={t('signIn.code.digitAriaLabel', { index: index + 1 })}
          aria-invalid={hasError ? 'true' : 'false'}
          // Keep password managers off the OTP boxes; native one-time-code
          // autofill above is preserved. Avoids injected icons shifting layout.
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={index === 0}
          wrapperClassName="ai-sign-in-modal__code-cell"
          className="ai-sign-in-modal__code-input"
          onChange={event => handleChange(index, event.target.value)}
          onKeyDown={event => handleKeyDown(index, event)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  )
}

Inputs.propTypes = {
  digits: arrayOf(string).isRequired,
  hasError: bool.isRequired,
  onDigitsChange: func.isRequired,
}

export default Inputs
